import * as THREE from 'three';
import { fishSpecies, RARE_BONUS, RARE_BONUS_APPLIES, type FishDef, type WaterKind, type WeatherKind } from '../data/fish';
import { itemDef } from '../data/items';
import type { Player } from '../player/Player';
import type { World } from '../world/World';
import { WATER_LEVEL } from '../world/WorldGen';

export type FishingState = 'idle' | 'waiting' | 'bite';
export interface CatchResult { fish: FishDef; }

/** Client-local fishing minigame. Catch inventory/progression stays personal. */
export class Fishing {
  state: FishingState = 'idle';
  private timer = 0;
  private biteWindow = 0;
  private water: WaterKind = 'river';
  private bobber: THREE.Mesh | null = null;

  get active(): boolean { return this.state !== 'idle'; }
  get label(): string | null { return this.state === 'waiting' ? '🎣 Balık bekleniyor…' : this.state === 'bite' ? '🐟 ISIRDI! E / Etkileşim ile sar!' : null; }

  cast(world: World, player: Player, random = Math.random): { ok: boolean; message?: string } {
    if (this.active) return { ok: false, message: 'Olta zaten suda' };
    const held = player.inventory.held();
    const tool = held ? itemDef(held.item).tool : null;
    if (tool?.class !== 'rod') return { ok: false, message: 'Olta gerekli' };
    const point = player.position.clone().add(player.forward().setY(0).normalize().multiplyScalar(10));
    if (!world.isWaterAt(point.x, point.z) && !world.isRiverAt(point.x, point.z)) return { ok: false, message: 'Oltayı suya doğru at' };
    this.water = waterKind(world, point.x, point.z);
    this.timer = 2.5 + random() * (tool.speed > 1 ? 2.6 : 4.5);
    this.state = 'waiting';
    this.bobber = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), new THREE.MeshBasicMaterial({ color: 0xff4d4d }));
    this.bobber.position.set(point.x, WATER_LEVEL + 0.13, point.z);
    world.scene.add(this.bobber);
    return { ok: true };
  }

  update(dt: number): 'bite' | 'missed' | null {
    if (this.state === 'idle') return null;
    if (this.bobber) this.bobber.position.y = WATER_LEVEL + 0.13 + Math.sin(performance.now() * 0.008) * (this.state === 'bite' ? 0.12 : 0.035);
    if (this.state === 'waiting') { this.timer -= dt; if (this.timer <= 0) { this.state = 'bite'; this.biteWindow = 1.7; return 'bite'; } }
    else { this.biteWindow -= dt; if (this.biteWindow <= 0) { this.clear(); return 'missed'; } }
    return null;
  }

  reel(player: Player, night: boolean, weather: WeatherKind = 'clear', random = Math.random): CatchResult | null {
    if (this.state !== 'bite') { if (this.active) this.clear(); return null; }
    const held = player.inventory.held(); const tool = held ? itemDef(held.item).tool : null;
    if (tool?.class !== 'rod') { this.clear(); return null; }
    const fish = rollCatch(this.water, night, tool.speed > 1 ? 2 : 1, weather, random);
    if (!fish || player.inventory.add(fish.id, 1) > 0) { this.clear(); return null; }
    player.inventory.wearHeld(1); this.clear(); return { fish };
  }

  clear(): void { this.state = 'idle'; this.timer = 0; this.biteWindow = 0; if (this.bobber) { this.bobber.removeFromParent(); this.bobber.geometry.dispose(); (this.bobber.material as THREE.Material).dispose(); this.bobber = null; } }
}

export function waterKind(world: World, x: number, z: number): WaterKind { if (world.isRiverAt(x,z)) return 'river'; const biome=world.biomeAt(x,z).biome.id; if (biome==='beach') return 'sea'; if (biome==='swamp') return 'swamp'; if (biome==='ice') return 'ice'; if (biome==='venom') return 'venom'; return 'lake'; }
export function rollCatch(water: WaterKind, night: boolean, rodLevel: number, weather: WeatherKind = 'clear', random = Math.random): FishDef | null { const candidates=fishSpecies.all().filter(f=>f.waters.includes(water)&&f.rodLevel<=rodLevel&&(f.time==='any'||(f.time==='night')===night)); if(!candidates.length)return null; const weighted=candidates.map(f=>({f,w:f.weight*rareMultiplier(f,night,rodLevel)*(f.weatherBonus?.[weather] ?? 1)})); const total=weighted.reduce((s,x)=>s+x.w,0); let roll=random()*total; for(const x of weighted){roll-=x.w;if(roll<=0)return x.f;} return weighted.at(-1)!.f; }
function rareMultiplier(f:FishDef,night:boolean,rodLevel:number):number { let m=1; if(f.rarity!=='common'){const nightM=night?1+(RARE_BONUS.night-1)*RARE_BONUS_APPLIES[f.rarity]:1; m*=Math.min(RARE_BONUS.maxCombined,nightM); if(rodLevel>=2)m*=1.5;} return m; }
